import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { GoogleAuthButton } from "@/components/GoogleAuthButton";

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [agreed, setAgreed] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const mismatch = password && confirmPassword && password !== confirmPassword;
    const tooShort = password.length > 0 && password.length < 8;

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!agreed) { setErr("Please accept the Terms and Privacy Policy to continue."); return; }
        if (password.length < 8) { setErr("Password must be at least 8 characters."); return; }
        if (password !== confirmPassword) { setErr("Passwords do not match."); return; }
        setErr(""); setBusy(true);
        const res = await register(name, email, password, confirmPassword);
        setBusy(false);
        if (res.ok) navigate("/onboarding");
        else setErr(res.error);
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-sand-50">
            <div className="hidden lg:flex relative bg-deep-ocean text-white p-14 flex-col justify-between">
                <Link to="/"><Logo /></Link>
                <div>
                    <h2 className="font-heading text-4xl tracking-tight max-w-md">Books that breathe like the trade winds.</h2>
                    <p className="mt-4 text-white/60 text-sm">Join the modern accounting platform built in the Cayman Islands.</p>
                </div>
                <div className="text-xs text-white/50">© {new Date().getFullYear()} BlueLedger</div>
            </div>
            <div className="flex flex-col justify-center px-6 sm:px-12 py-12 max-w-xl w-full mx-auto">
                <div className="lg:hidden mb-10"><Link to="/"><Logo /></Link></div>
                <h1 className="font-heading text-4xl text-ocean-700">Create your account</h1>
                <p className="mt-2 text-muted-foreground">Free forever for solo founders. No card required.</p>

                <div className="mt-8">
                    <GoogleAuthButton testId="register-google-btn" />
                </div>
                <div className="relative my-6" aria-hidden="true">
                    <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-sand-300" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase tracking-wider">
                        <span className="bg-sand-50 px-3 text-muted-foreground">or sign up with email</span>
                    </div>
                </div>

                <form onSubmit={onSubmit} className="space-y-5">
                    <div>
                        <Label htmlFor="name">Full name</Label>
                        <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="register-name" className="mt-1.5 h-11" />
                    </div>
                    <div>
                        <Label htmlFor="email">Email</Label>
                        <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="register-email" className="mt-1.5 h-11" />
                    </div>
                    <div>
                        <Label htmlFor="password">Password</Label>
                        <Input id="password" type="password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="register-password" className="mt-1.5 h-11" />
                        {tooShort && <div className="text-xs text-coral-dark mt-1">Use 8+ characters.</div>}
                    </div>
                    <div>
                        <Label htmlFor="confirm-password">Confirm password</Label>
                        <Input
                            id="confirm-password"
                            type="password"
                            minLength={8}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            data-testid="register-confirm-password"
                            className={`mt-1.5 h-11 ${mismatch ? "border-coral-dark focus-visible:ring-coral-dark" : ""}`}
                        />
                        {mismatch && <div className="text-xs text-coral-dark mt-1" data-testid="register-password-mismatch">Passwords don't match.</div>}
                    </div>
                    <label className="flex items-start gap-3 text-xs text-muted-foreground cursor-pointer pt-1">
                        <input
                            type="checkbox"
                            checked={agreed}
                            onChange={(e) => setAgreed(e.target.checked)}
                            className="mt-0.5 h-4 w-4 rounded border-sand-400 text-secondary focus:ring-secondary"
                            data-testid="register-terms-checkbox"
                        />
                        <span>
                            I agree to the <Link to="/terms" target="_blank" className="text-secondary-dark hover:underline">Terms of Service</Link> and <Link to="/privacy" target="_blank" className="text-secondary-dark hover:underline">Privacy Policy</Link>.
                        </span>
                    </label>
                    {err && <div className="text-sm text-destructive" data-testid="register-error">{err}</div>}
                    <Button type="submit" className="w-full h-11 bg-ocean-700 hover:bg-ocean-700/90 text-white rounded-xl" disabled={busy || !agreed} data-testid="register-submit">
                        {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create account
                    </Button>
                </form>

                <p className="mt-8 text-sm text-muted-foreground">
                    Already have an account? <Link to="/login" className="text-secondary font-medium hover:underline">Sign in</Link>
                </p>
            </div>
        </div>
    );
}
